import React, { useState, useEffect, useMemo } from "react";
import { AlertCircle } from "lucide-react";
import { db } from "../../services/firebase";
import { doc, setDoc, collection, getDocs, getDoc } from "firebase/firestore";
import "./ProductRegistration.css";
import { useNavigate } from "react-router-dom";
import { useSeller } from "../../contexts/SellerContext";
import PageHeader from "../../components/PageHeader";
import ProductCard from "./ProductCard";
import ProductDetailsPopup from "./ProductDetailsPopup";
import CategoryAlert from "./CategoryAlert";
import ProductData, { PRIMARY_SEGMENTS, resolveSegmentKey } from "../SellerSegment";

const DEFAULT_PRODUCT_DETAILS = {
  purity: "",
  wastage: "",
  setMC: "",
  netGramMC: "",
  specification: "PLANE",
  specificationMC: "",
  specificationGramRate: "",
};

// Deep-clone a single product's entry (its styles + their details) so state
// updates never mutate the existing productData object in place.
const cloneProductEntry = (entry) => {
  const copy = { selectedStyleType: entry?.selectedStyleType ?? null };
  Object.keys(entry || {}).forEach((key) => {
    if (key === "selectedStyleType") return;
    const style = entry[key];
    if (style && typeof style === "object") {
      copy[key] = {
        ...style,
        details: { ...DEFAULT_PRODUCT_DETAILS, ...(style.details || {}) },
      };
    } else {
      copy[key] = style;
    }
  });
  return copy;
};

const ProductRegistration = () => {
  const nav = useNavigate();
  const { seller, updateSeller } = useSeller();
  const sellerId = seller?.sellerId;
  const Segment = seller?.segment?.toUpperCase();

  if (!sellerId) {
    nav("/login");
  }

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [showProductDetails, setShowProductDetails] = useState(false);
  const [currentProductForDetails, setCurrentProductForDetails] =
    useState(null);
  const [currentStyleType, setCurrentStyleType] = useState("Regular");
  const [productDetails, setProductDetails] = useState({
    purity: "",
    wastage: "",
    setMC: 0,
    netGramMC: 0,
    specification: "PLANE",
    specificationMC: "",
    specificationGramRate: "",
  });
  const [productSpecs, setProductSpecs] = useState([]);
  const [showCategoryAlert, setShowCategoryAlert] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [, setRegisteredProducts] = useState([]);
  const [uploading, setUploading] = useState(true);

  const purityLimits = {
    GOLD: {
      "916HUID": { min: 91.6, max: 92 },
      "750HUID": { min: 72, max: 75.5 },
      840: { min: 80, max: 88 },
      650: { min: 60, max: 70 },
      480: { min: 45, max: 48 },
    },
    SILVER: {
      925: { min: 90, max: 95 },
      999: { min: 95, max: 100 },
      800: { min: 75, max: 85 },
      900: { min: 85, max: 95 },
    },
    PLATINUM: {
      950: { min: 90, max: 98 },
      900: { min: 85, max: 95 },
      850: { min: 80, max: 90 },
    },
    DIAMOND: {
      "18KARAT": { min: 75, max: 76 },
      "14KARAT": { min: 58, max: 60 },
      "9KARAT": { min: 38, max: 40 },
    },
    GEMS: {
      Emerald: { min: 0, max: 100 },
      Ruby: { min: 0, max: 100 },
      Sapphire: { min: 0, max: 100 },
      Topaz: { min: 0, max: 100 },
    },
    PEARLS: {
      Freshwater: { min: 0, max: 100 },
      Akoya: { min: 0, max: 100 },
      Tahitian: { min: 0, max: 100 },
      "South Sea": { min: 0, max: 100 },
    },
  };

  // Extract canonical segments from SellerSegment
  const segments = useMemo(() => PRIMARY_SEGMENTS, []);

  // Resolve segment key to match ProductData exactly (handling aliases like GEM/GEMS, TOOL/TOOLS)
  const resolvedSegment = useMemo(() => {
    if (!Segment) return "";
    return resolveSegmentKey(Segment) || Segment.toUpperCase();
  }, [Segment]);

  const categoriesBySegment = useMemo(() => {
    const result = {};
    if (!ProductData) return result;
    Object.keys(ProductData).forEach((segment) => {
      const cats = Object.keys(ProductData[segment] || {});
      result[segment] = cats;
      result[segment.toUpperCase()] = cats;
    });
    // Support aliases so categoriesBySegment[Segment] always works
    if (result["GEM"]) result["GEMS"] = result["GEM"];
    if (result["PEARL"]) result["PEARLS"] = result["PEARL"];
    if (result["DIAMOND"]) result["DIAMONDS"] = result["DIAMOND"];
    return result;
  }, []);

  const getSubcategories = useMemo(() => {
    if (!resolvedSegment || !selectedCategory || !ProductData) return [];
    const segData = ProductData[resolvedSegment];
    if (!segData) return [];

    const catData =
      segData[selectedCategory] ||
      segData[String(selectedCategory).trim()] ||
      Object.entries(segData).find(
        ([k]) =>
          k.trim().toUpperCase() ===
          String(selectedCategory).trim().toUpperCase(),
      )?.[1];

    if (!catData || typeof catData !== "object") return [];
    return Object.keys(catData);
  }, [resolvedSegment, selectedCategory]);

  // Get available specifications for a specific product and finish type
  const getAvailableSpecifications = (productName, finishType) => {
    if (
      !resolvedSegment ||
      !selectedCategory ||
      !selectedSubcategory ||
      !productName ||
      !finishType
    ) {
      return ["PLANE", "MEENAWORK", "STONEWORK", "OTHERWORK"];
    }

    const segData = ProductData[resolvedSegment];
    const catData =
      segData?.[selectedCategory] ||
      Object.entries(segData || {}).find(
        ([k]) =>
          k.trim().toUpperCase() ===
          String(selectedCategory).trim().toUpperCase(),
      )?.[1];

    const productInfo =
      catData?.[selectedSubcategory]?.[productName] ||
      Object.entries(catData?.[selectedSubcategory] || {}).find(
        ([k]) =>
          k.trim().toUpperCase() === String(productName).trim().toUpperCase(),
      )?.[1];

    return (
      productInfo?.finishTypes?.[finishType] || [
        "PLANE",
        "MEENAWORK",
        "STONEWORK",
        "OTHERWORK",
      ]
    );
  };

  const allProducts = useMemo(() => {
    const products = [];
    let id = 1;

    segments.forEach((segment) => {
      const categories = Object.keys(ProductData[segment] || {});
      categories.forEach((category) => {
        const subcategories = Object.keys(ProductData[segment][category] || {});
        subcategories.forEach((subcategory) => {
          const productNames = Object.keys(
            ProductData[segment][category][subcategory] || {},
          );
          productNames.forEach((name) => {
            if (
              name === "image" ||
              name === "note" ||
              name === "sizing_unit" ||
              name === "options" ||
              name === "color"
            ) {
              return;
            }

            const productInfo =
              ProductData[segment]?.[category]?.[subcategory]?.[name];

            if (!productInfo || typeof productInfo !== "object") {
              return;
            }

            const styleTypes = Object.keys(productInfo?.finishTypes || {});

            products.push({
              id: id++,
              name,
              image: `https://placehold.co/60x60/E0E0E0/333333?text=${name[0]}`,
              segment,
              categoryId: category,
              subcategoryId: subcategory,
              styleTypes,
              productInfo,
            });
          });
        });
      });
    });
    return products;
  }, [segments]);

  useEffect(() => {
    const fetchProductSpecs = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "productSpecs"));
        const specsData = [];
        querySnapshot.forEach((doc) => {
          specsData.push({ id: doc.id, ...doc.data() });
        });
        console.log("specs data", specsData);
        setProductSpecs(specsData);
      } catch (error) {
        console.error("Error fetching product specs:", error);
      }
    };
    fetchProductSpecs();
  }, []);

  useEffect(() => {
    const fetchRegisteredProducts = async () => {
      if (!sellerId) return;
      try {
        const docRef = doc(db, "ProductRegistrations", sellerId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          const approved =
            data.registrations?.filter((reg) => reg.status === "approved") ||
            [];
          setRegisteredProducts(approved);
        }
      } catch (error) {
        console.error("Error fetching registered products:", error);
      }
    };
    fetchRegisteredProducts();
  }, [sellerId]);

  const [productData, setProductData] = useState(() => {
    const initialProductData = {};
    allProducts.forEach((product) => {
      const productEntry = { selectedStyleType: null };

      product.styleTypes.forEach((styleType) => {
        productEntry[styleType] = {
          selected: false,
          image: null,
          details: { ...DEFAULT_PRODUCT_DETAILS },
        };
      });

      initialProductData[product.id] = productEntry;
    });
    return initialProductData;
  });

  // Single source of truth for products that are "ready to register": a product
  // counts once it has a selected style with saved details (purity). This is
  // exactly the set that handleSendToQC submits, so the count and the payload
  // can never drift apart.
  const selectedProducts = useMemo(() => {
    const list = [];
    allProducts.forEach((product) => {
      const entry = productData[product.id];
      const styleType = entry?.selectedStyleType;
      if (!styleType) return;
      const styleData = entry[styleType];
      if (styleData?.selected && styleData?.details?.purity) {
        list.push({
          productId: product.id,
          styleType: styleType,
          productName: product.name,
        });
      }
    });
    return list;
  }, [productData, allProducts]);

  // Normalize helper: case-insensitive string comparison for Firestore field lookups
  const normalizeStr = (val) =>
    String(val || "")
      .trim()
      .toUpperCase();

  const isCategoryConfigured = (segment, category, subcategory) => {
    return productSpecs.some(
      (spec) =>
        normalizeStr(spec.segment) === normalizeStr(segment) &&
        normalizeStr(spec.category) === normalizeStr(category) &&
        normalizeStr(spec.productSource) === normalizeStr(subcategory),
    );
  };

  const getCategoryLimits = (
    segment,
    category,
    subcategory,
    productName,
    styleType = null,
    selectedSpecification = null,
  ) => {
    if (productName && styleType && selectedSpecification) {
      const exactMatch = productSpecs.find(
        (spec) =>
          normalizeStr(spec.segment) === normalizeStr(segment) &&
          normalizeStr(spec.category) === normalizeStr(category) &&
          normalizeStr(spec.productSource) === normalizeStr(subcategory) &&
          normalizeStr(spec.productName) === normalizeStr(productName) &&
          normalizeStr(spec.finishType) === normalizeStr(styleType) &&
          normalizeStr(spec.specification) ===
            normalizeStr(selectedSpecification),
      );
      if (exactMatch) return exactMatch;
    }

    if (productName && styleType) {
      const finishMatch = productSpecs.find(
        (spec) =>
          normalizeStr(spec.segment) === normalizeStr(segment) &&
          normalizeStr(spec.category) === normalizeStr(category) &&
          normalizeStr(spec.productSource) === normalizeStr(subcategory) &&
          normalizeStr(spec.productName) === normalizeStr(productName) &&
          normalizeStr(spec.finishType) === normalizeStr(styleType),
      );
      if (finishMatch) return finishMatch;
    }

    if (productName) {
      const productMatch = productSpecs.find(
        (spec) =>
          normalizeStr(spec.segment) === normalizeStr(segment) &&
          normalizeStr(spec.category) === normalizeStr(category) &&
          normalizeStr(spec.productSource) === normalizeStr(subcategory) &&
          normalizeStr(spec.productName) === normalizeStr(productName),
      );
      if (productMatch) return productMatch;
    }

    const categorySpecs = productSpecs.find(
      (spec) =>
        normalizeStr(spec.segment) === normalizeStr(segment) &&
        normalizeStr(spec.category) === normalizeStr(category) &&
        normalizeStr(spec.productSource) === normalizeStr(subcategory),
    );
    return categorySpecs || null;
  };

  const validateSpecificationConstraint = (
    field,
    value,
    categoryLimits,
    specType,
  ) => {
    if (!categoryLimits) return true;
    let maxValue = null;
    let fieldName = "";

    if (field === "mc") {
      maxValue = categoryLimits.maxSpecificationMakingSeller;
      fieldName = "Specification Wastage";
    } else if (field === "gramRate") {
      maxValue = categoryLimits.maxSpecificationGramRateSeller;
      fieldName = "Specification Gram Rate";
    }

    const numMaxValue = parseFloat(maxValue);
    const numValue = parseFloat(value);
    if (!isNaN(numMaxValue) && !isNaN(numValue) && numValue > numMaxValue) {
      return `${fieldName} cannot exceed ${numMaxValue} for ${specType}`;
    }
    return true;
  };

  const calculateTotalWastage = (details) => {
    const baseWastage = parseFloat(details.wastage) || 0;
    if (details.specification === "PLANE") {
      return baseWastage;
    }
    const specMC = parseFloat(details.specificationMC) || 0;
    return baseWastage + (specMC > 0 ? specMC : 0);
  };

  const calculateTotalMakingCharges = (details) => {
    const setMC = parseFloat(details.setMC) || 0;
    const netGramMC = parseFloat(details.netGramMC) || 0;
    return setMC + netGramMC;
  };

  const filteredProducts = useMemo(() => {
    if (!Segment || !selectedCategory || !selectedSubcategory) {
      return [];
    }
    return allProducts.filter(
      (product) =>
        product.segment === Segment &&
        product.categoryId === selectedCategory &&
        product.subcategoryId === selectedSubcategory,
    );
  }, [Segment, selectedCategory, selectedSubcategory, allProducts]);

  const handleStyleSelect = (productId, styleType) => {
    const product = allProducts.find((p) => p.id === productId);
    if (!isCategoryConfigured(Segment, selectedCategory, selectedSubcategory)) {
      setShowCategoryAlert(true);
      return;
    }

    const productEntry = productData[productId];
    const hasAnyImage = product?.styleTypes.some(
      (st) => productEntry[st]?.image,
    );

    if (!hasAnyImage) {
      alert(
        "Please upload at least one product image before selecting a style type.",
      );
      return;
    }
    const newEntry = cloneProductEntry(productEntry);
    const currentSelectedStyle = newEntry.selectedStyleType;

    if (currentSelectedStyle === styleType) {
      // Same style clicked - just open the popup to view/edit
      const currentStyleDetails = newEntry[currentSelectedStyle]?.details;
      setCurrentProductForDetails(productId);
      setCurrentStyleType(styleType);
      setProductDetails({
        ...DEFAULT_PRODUCT_DETAILS,
        ...currentStyleDetails,
        specification: currentStyleDetails?.specification || "PLANE",
      });
      setValidationErrors({});
      setShowProductDetails(true);
      return;
    }

    // Different style clicked
    if (currentSelectedStyle) {
      const currentStyleDetails = newEntry[currentSelectedStyle]?.details;
      const hasFilledDetails =
        currentStyleDetails &&
        (currentStyleDetails.purity ||
          currentStyleDetails.wastage ||
          currentStyleDetails.setMC ||
          currentStyleDetails.netGramMC ||
          currentStyleDetails.specificationMC ||
          currentStyleDetails.specificationGramRate);

      if (hasFilledDetails) {
        alert(
          "Please clear the current style details before switching to another style type.",
        );
        return;
      }

      newEntry[currentSelectedStyle].selected = false;
    }

    newEntry.selectedStyleType = styleType;
    newEntry[styleType].selected = true;
    // Immutable update: only this product's entry is replaced, so every other
    // product's selection is preserved.
    setProductData((prev) => ({ ...prev, [productId]: newEntry }));

    setCurrentProductForDetails(productId);
    setCurrentStyleType(styleType);
    setProductDetails({
      ...DEFAULT_PRODUCT_DETAILS,
      ...newEntry[styleType].details,
      specification: newEntry[styleType].details?.specification || "PLANE",
    });
    setValidationErrors({});
    setShowProductDetails(true);
  };

  const getImageUrl = (productId) => {
    const selectedStyle = productData[productId].selectedStyleType;
    if (selectedStyle && productData[productId][selectedStyle]) {
      return productData[productId][selectedStyle].image;
    }
    const product = allProducts.find((p) => p.id === productId);
    if (product && product.styleTypes.length > 0) {
      return productData[productId][product.styleTypes[0]]?.image;
    }
    return null;
  };

  const getCurrentDetails = (productId) => {
    const selectedStyle = productData[productId].selectedStyleType;
    if (selectedStyle && productData[productId][selectedStyle]) {
      return productData[productId][selectedStyle].details;
    }
    const product = allProducts.find((p) => p.id === productId);
    if (product && product.styleTypes.length > 0) {
      return productData[productId][product.styleTypes[0]]?.details;
    }
    return null;
  };

  const validateSquareImage = (file) => {
    return new Promise((resolve) => {
      if (!file.type.startsWith("image/")) {
        resolve({
          valid: false,
          file,
          error: `"${file.name}" is not an image file.`,
        });
        return;
      }

      const img = new Image();
      const objectUrl = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        const width = img.naturalWidth;
        const height = img.naturalHeight;
        const ratio = width / height;
        const isOneToOne = Math.abs(ratio - 1) <= 0.01;

        if (isOneToOne) {
          resolve({ valid: true, file, width, height });
        } else {
          resolve({
            valid: false,
            file,
            width,
            height,
            error: `"${file.name}" is ${width}x${height} (${ratio.toFixed(
              2,
            )}:1). Only 1:1 (square) images are allowed.`,
          });
        }
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        resolve({
          valid: false,
          file,
          error: `Failed to load image "${file.name}".`,
        });
      };

      img.src = objectUrl;
    });
  };

  const handleImageUpload = async (event, productId) => {
    if (!isCategoryConfigured(Segment, selectedCategory, selectedSubcategory)) {
      setShowCategoryAlert(true);
      return;
    }

    event.stopPropagation();
    const file = event.target.files[0];
    if (!file) return;

    const inputElement = event.target;

    const validation = await validateSquareImage(file);
    if (!validation.valid) {
      alert(`Only 1:1 (square) images are allowed!\n\n${validation.error}`);
      inputElement.value = "";
      return;
    }

    const product = allProducts.find((p) => p.id === productId);
    const CLOUDINARY_CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
    const CLOUDINARY_UPLOAD_PRESET = "jmiseller";
    const CLOUDINARY_API_URL = `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", CLOUDINARY_UPLOAD_PRESET);

    try {
      const response = await fetch(CLOUDINARY_API_URL, {
        method: "POST",
        body: formData,
      });

      const data = await response.json();
      if (data.secure_url) {
        // Keep every other product's selection intact - only touch this product.
        setProductData((prev) => {
          const entry = cloneProductEntry(prev[productId]);

          product.styleTypes.forEach((styleType) => {
            entry[styleType].image = data.secure_url;
          });

          // Auto-select the first style only if this product has none chosen yet.
          const firstStyleType = product.styleTypes[0];
          if (!entry.selectedStyleType && firstStyleType) {
            entry.selectedStyleType = firstStyleType;
            entry[firstStyleType].selected = true;
          }

          return { ...prev, [productId]: entry };
        });
      }
    } catch (error) {
      console.error("Error uploading image:", error);
    } finally {
      inputElement.value = "";
    }
  };

  const handleCardClick = (productId) => {
    if (!isCategoryConfigured(Segment, selectedCategory, selectedSubcategory)) {
      setShowCategoryAlert(true);
      return;
    }

    const product = allProducts.find((p) => p.id === productId);
    if (
      !productData[productId].selectedStyleType &&
      product.styleTypes.length > 0
    ) {
      handleStyleSelect(productId, product.styleTypes[0]);
    }
  };

  const handleDeleteStyle = (productId, styleType) => {
    if (!window.confirm("Are you sure you want to delete this style type?")) {
      return;
    }

    setProductData((prev) => {
      const entry = cloneProductEntry(prev[productId]);
      const isCurrentSelected = entry.selectedStyleType === styleType;

      entry[styleType].details = { ...DEFAULT_PRODUCT_DETAILS };

      if (isCurrentSelected) {
        entry.selectedStyleType = null;
        entry[styleType].selected = false;
      }

      return { ...prev, [productId]: entry };
    });

    setValidationErrors({});
    setShowProductDetails(false);
  };

  const handleSendToQC = async () => {
    if (!sellerId) {
      alert("Seller ID not found. Please log in again.");
      return;
    }

    if (!isCategoryConfigured(Segment, selectedCategory, selectedSubcategory)) {
      setShowCategoryAlert(true);
      return;
    }

    if (selectedProducts.length === 0) {
      alert(
        "Please select at least one product with details before submitting.",
      );
      return;
    }

    // Every submitted product must have at least one photo.
    const productsMissingImage = selectedProducts.filter(({ productId }) => {
      const product = allProducts.find((p) => p.id === productId);
      const entry = productData[productId];
      return !product?.styleTypes.some((st) => entry[st]?.image);
    });
    if (productsMissingImage.length > 0) {
      alert(
        "Please upload at least one product photo for every selected product before submitting.",
      );
      return;
    }

    setUploading(false);
    try {
      const timestamp = Date.now();
      const docRef = doc(db, "ProductRegistrations", sellerId);

      const docSnap = await getDoc(docRef);
      const existingRegistrations = docSnap.exists()
        ? docSnap.data().registrations || []
        : [];

      // Build one registration per ready product, using each product's own
      // segment/category/subcategory so selections made across categories are
      // filed correctly.
      const newRegistrations = selectedProducts.map(
        ({ productId, styleType, productName }) => {
          const meta = allProducts.find((p) => p.id === productId);
          const styleData = productData[productId][styleType];
          const { segment, categoryId, subcategoryId } = meta || {};

          return {
            registrationId: `${sellerId}_${productId}_${styleType}_${timestamp}`,
            status: "pending_approval",
            approved: false,
            requestTimestamp: new Date(),
            segment: segment,
            category: categoryId,
            productSource: subcategoryId,
            products: {
              [productName]: {
                productId: productId,
                segment: segment,
                category: categoryId,
                productSource: subcategoryId,
                purity: styleData.details.purity,
                wastage: styleData.details.wastage,
                setMC: styleData.details.setMC,
                netGramMC: styleData.details.netGramMC,
                specification: styleData.details.specification,
                specificationMC: styleData.details.specificationMC || "",
                specificationGramRate:
                  styleData.details.specificationGramRate || "",
                image: styleData.image || "",
                selectedStyleType: styleType,
              },
            },
          };
        },
      );

      if (newRegistrations.length === 0) {
        alert("No valid products with complete details selected.");
        return;
      }

      const updatedRegistrations = [...existingRegistrations];

      newRegistrations.forEach((newReg) => {
        const productName = Object.keys(newReg.products)[0];
        const newProduct = newReg.products[productName];

        const registrationKey = `${newReg.segment}_${newReg.category}_${newReg.productSource}_${productName}_${newProduct.selectedStyleType}_${newProduct.specification}`;

        const existingIndex = updatedRegistrations.findIndex((existingReg) => {
          const existingProductName = Object.keys(
            existingReg.products || {},
          )[0];
          const existingProductData = existingReg.products[existingProductName];

          if (!existingProductData) return false;

          const existingKey = `${existingReg.segment}_${existingReg.category}_${existingReg.productSource}_${existingProductName}_${existingProductData.selectedStyleType}_${existingProductData.specification}`;
          return existingKey === registrationKey;
        });

        if (existingIndex !== -1) {
          updatedRegistrations[existingIndex] = {
            ...updatedRegistrations[existingIndex],
            ...newReg,
            registrationId: updatedRegistrations[existingIndex].registrationId,
            requestTimestamp: new Date(),
            status: "pending_approval",
          };
        } else {
          updatedRegistrations.push(newReg);
        }
      });

      const saveData = {
        sellerId: sellerId,
        registrations: updatedRegistrations,
        ...(docSnap.exists()
          ? { lastUpdated: new Date() }
          : {
              createdAt: new Date(),
              lastUpdated: new Date(),
            }),
      };

      await setDoc(docRef, saveData);

      const sellerProfileRef = doc(db, "profile", sellerId);
      await setDoc(
        sellerProfileRef,
        {
          ProductRegistration: true,
        },
        { merge: true },
      );

      // Reflect the completed step in the app state so route guards
      // (e.g. /qcapprovalpage) treat the registration as finished.
      updateSeller({ productRegistration: true });

      nav("/QCApprovalPage", {
        state: { totalSelected: selectedProducts.length },
      });

      // Clear only the products that were just submitted; anything else the
      // seller had selected stays intact.
      setProductData((prev) => {
        const next = { ...prev };
        selectedProducts.forEach(({ productId, styleType }) => {
          const entry = cloneProductEntry(prev[productId]);
          entry.selectedStyleType = null;
          entry[styleType].selected = false;
          next[productId] = entry;
        });
        return next;
      });
    } catch (error) {
      console.error("Error sending data for approval:", error);
      alert("Error submitting product registration. Please try again.");
    } finally {
      setUploading(true);
    }
  };

  const handleSaveProductDetails = () => {
    const currentProduct = allProducts.find(
      (p) => p.id === currentProductForDetails,
    );
    const categoryLimits = getCategoryLimits(
      Segment,
      selectedCategory,
      selectedSubcategory,
      currentProduct.name,
      currentStyleType,
      productDetails.specification,
    );
    const errors = {};
    const segmentPurityLimits = purityLimits[Segment] || {};
    const categoryPurityLimits = segmentPurityLimits[selectedCategory] || {
      min: 0,
      max: 100,
    };

    const purityValue = productDetails.purity;
    if (purityValue === "" || purityValue === 0) {
      errors.purity = `Purity is required`;
    } else {
      const parsedPurity = parseFloat(purityValue);
      if (
        isNaN(parsedPurity) ||
        parsedPurity < categoryPurityLimits.min ||
        parsedPurity > categoryPurityLimits.max
      ) {
        errors.purity = `Purity must be between ${categoryPurityLimits.min} and ${categoryPurityLimits.max} for ${Segment} - ${selectedCategory}`;
      }
    }
    if (productDetails.wastage === "" || productDetails.wastage === 0) {
      errors.totalWastage = `Wastage is required`;
    }
    if (categoryLimits) {
      const totalWastage = calculateTotalWastage(productDetails);
      const maxWastageSeller = parseFloat(categoryLimits.maxWastageSeller) || 0;
      const maxSpecificationMakingSeller =
        parseFloat(categoryLimits.maxSpecificationMakingSeller) || 0;
      const totalWastageLimit = maxWastageSeller + maxSpecificationMakingSeller;
      if (totalWastage > totalWastageLimit) {
        errors.totalWastage = `Total wastage (${totalWastage.toFixed(
          2,
        )}%) cannot exceed ${totalWastageLimit}%`;
      }

      const totalMaking = calculateTotalMakingCharges(productDetails);
      const maxSetMakingSeller =
        parseFloat(categoryLimits.maxSetMakingSeller) || 0;
      const maxGramMakingSeller =
        parseFloat(categoryLimits.maxGramMakingSeller) || 0;
      const totalMakingLimit = maxSetMakingSeller + maxGramMakingSeller;
      if (totalMaking > totalMakingLimit) {
        errors.totalMaking = `Total making charges (${totalMaking.toFixed(
          2,
        )}) cannot exceed ${totalMakingLimit}`;
      }

      if (productDetails.specification === "PLANE") {
        const setMCValue = parseFloat(productDetails.setMC);
        const setMCNum = isNaN(setMCValue) ? 0 : setMCValue;
        const maxSetMakingSeller = parseFloat(
          categoryLimits.maxSetMakingSeller,
        );
        if (setMCNum > maxSetMakingSeller) {
          errors.setMC = `Set making charges cannot exceed ${categoryLimits.maxSetMakingSeller}`;
        }
        const netGramMCValue = parseFloat(productDetails.netGramMC);
        const netGramMCNum = isNaN(netGramMCValue) ? 0 : netGramMCValue;
        const maxGramMakingSeller = parseFloat(
          categoryLimits.maxGramMakingSeller,
        );
        if (netGramMCNum > maxGramMakingSeller) {
          errors.netGramMC = `Net Gram MC cannot exceed ${categoryLimits.maxGramMakingSeller}`;
        }
      } else {
        const specType = productDetails.specification;
        const specMCError = validateSpecificationConstraint(
          "mc",
          productDetails.specificationMC,
          categoryLimits,
          specType,
        );
        if (specMCError !== true) {
          errors.specificationMC = specMCError;
        }
        const specGramRateError = validateSpecificationConstraint(
          "gramRate",
          productDetails.specificationGramRate,
          categoryLimits,
          specType,
        );
        if (specGramRateError !== true) {
          errors.specificationGramRate = specGramRateError;
        }
      }
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    setValidationErrors({});
    setProductData((prev) => {
      const entry = cloneProductEntry(prev[currentProductForDetails]);
      entry[currentStyleType].details = { ...productDetails };
      entry[currentStyleType].selected = true;
      entry.selectedStyleType = currentStyleType;
      return { ...prev, [currentProductForDetails]: entry };
    });
    setShowProductDetails(false);
  };

  const getTotalSelected = () => {
    return selectedProducts.length;
  };

  const hasStyleSelected = (productId) => {
    return productData[productId].selectedStyleType !== null;
  };

  return (
    <>
      <PageHeader title="Product Registration" />
      <div className="app-container">
        <div className="main-content">
          <p className="sub-heading">
            Choose your product category, sub-type, and styles to begin listing.
          </p>

          <div className="form-group">
            <label className="form-label">Segment</label>
            <div className="horizontal-scroll-container">
              <button className="category-button selected">{Segment}</button>
            </div>
          </div>

          {Segment && (
            <div className="form-group">
              <label className="form-label">Category</label>
              <div className="horizontal-scroll-container">
                {categoriesBySegment[Segment]?.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => {
                      setSelectedCategory(cat);
                      setSelectedSubcategory(null);
                    }}
                    className={`category-button ${
                      selectedCategory === cat ? "selected" : ""
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>
          )}

          {selectedCategory && (
            <div className="form-group">
              <label className="form-label">Subcategory</label>
              <div className="horizontal-scroll-container">
                {getSubcategories.map((sub) => (
                  <button
                    key={sub}
                    onClick={() => setSelectedSubcategory(sub)}
                    className={`category-button ${
                      selectedSubcategory === sub ? "selected" : ""
                    } ${
                      !isCategoryConfigured(Segment, selectedCategory, sub)
                        ? "not-configured"
                        : ""
                    }`}
                    title={
                      !isCategoryConfigured(Segment, selectedCategory, sub)
                        ? "Segment/Category/Subcategory not configured - Contact JMI"
                        : ""
                    }
                  >
                    {sub}
                    {!isCategoryConfigured(Segment, selectedCategory, sub) && (
                      <AlertCircle size={14} />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          <h3 className="registraion-section-title">Select Products</h3>

          {Segment && selectedCategory && selectedSubcategory ? (
            !isCategoryConfigured(
              Segment,
              selectedCategory,
              selectedSubcategory,
            ) ? (
              <div className="category-warning">
                <AlertCircle size={24} />
                <p>
                  This segment/category/subcategory combination is not
                  configured. Please contact JMI to configure "{Segment} /{" "}
                  {selectedCategory} / {selectedSubcategory}" before proceeding.
                </p>
              </div>
            ) : filteredProducts.length > 0 ? (
              <div className="products-Rgrid">
                {filteredProducts.map((product) => {
                  return (
                    <ProductCard
                      key={product.id}
                      product={product}
                      productData={productData}
                      selectedCategory={selectedCategory}
                      selectedSubcategory={selectedSubcategory}
                      Segment={Segment}
                      filteredProducts={filteredProducts}
                      allProducts={allProducts}
                      hasStyleSelected={hasStyleSelected}
                      getCurrentDetails={getCurrentDetails}
                      getCurrentImage={getImageUrl}
                      calculateTotalWastage={calculateTotalWastage}
                      handleCardClick={handleCardClick}
                      handleStyleSelect={handleStyleSelect}
                      handleImageUpload={handleImageUpload}
                      handleDeleteStyle={handleDeleteStyle}
                      styleTypes={product.styleTypes}
                      isCategoryConfigured={isCategoryConfigured}
                    />
                  );
                })}
              </div>
            ) : (
              <p className="no-results-message">
                No products found for the selected segment, category, and
                subcategory.
              </p>
            )
          ) : (
            <p className="no-results-message">
              Please select a segment, category, and subcategory to view
              products.
            </p>
          )}
        </div>

        <div className="sticky-footer">
          <span className="selected-products-count">
            Selected: {getTotalSelected()} Products
          </span>
          <button
            className="send-to-qc-button"
            onClick={handleSendToQC}
            disabled={
              !uploading ||
              selectedProducts.length === 0 ||
              !isCategoryConfigured(
                Segment,
                selectedCategory,
                selectedSubcategory,
              )
            }
          >
            Send to QC
          </button>
        </div>

        <ProductDetailsPopup
          showProductDetails={showProductDetails}
          setShowProductDetails={setShowProductDetails}
          currentProductForDetails={currentProductForDetails}
          currentStyleType={currentStyleType}
          productDetails={productDetails}
          setProductDetails={setProductDetails}
          allProducts={allProducts}
          Segment={Segment}
          selectedCategory={selectedCategory}
          selectedSubcategory={selectedSubcategory}
          productSpecs={productSpecs}
          getCategoryLimits={getCategoryLimits}
          getAvailableSpecifications={getAvailableSpecifications}
          purityLimits={purityLimits}
          handleSaveProductDetails={handleSaveProductDetails}
          validationErrors={validationErrors}
          setValidationErrors={setValidationErrors}
          handleImageUpload={handleImageUpload}
          productData={productData}
          calculateTotalWastage={calculateTotalWastage}
          calculateTotalMakingCharges={calculateTotalMakingCharges}
        />
        <CategoryAlert
          showCategoryAlert={showCategoryAlert}
          setShowCategoryAlert={setShowCategoryAlert}
          Segment={Segment}
          selectedCategory={selectedCategory}
          selectedSubcategory={selectedSubcategory}
        />
      </div>
    </>
  );
};

export default ProductRegistration;
